import {Component, HostBinding, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {VERSION} from '../environments/version';
import {APP_NAME, REVOKE_TOKEN} from 'shared/magic';
import {select, Store} from '@ngrx/store';
import * as userActions from './_actions/user.actions';
import * as rootActions from './_actions/root.actions';
import {getCurrentRouteState} from 'app/_selectors/router.selector';
import {NgbPopoverConfig} from '@ng-bootstrap/ng-bootstrap';
import {POPOVER_CONFIG} from 'shared/defaults';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LayoutService} from '../../projects/layout/src/lib/layout.service';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {selectVersionStatus} from 'app/_selectors/user.selectors';
import {AuthService} from 'app/auth/auth.service';
import {finalize, take} from 'rxjs/operators';
import {TabCountService} from 'shared/_services/tab-count.service';

@UntilDestroy()
@Component({
	selector: 'app-root',
	template: `
		<div class="splash">
			<div class="splash-spinner">
				<div class="spinner-border" role="status">
					<span class="sr-only">Loading...</span>
				</div>
			</div>
			<div class="splash-overlay"></div>
		</div>
		<ng-progress></ng-progress>
		<router-outlet></router-outlet>
		<layout-footer></layout-footer>
		<app-modal></app-modal>

		<ng-template #content let-modal>
			<ng-container *transloco="let t; read: 'version'">
				<div class="modal-header">
					<h4 class="modal-title text-center" id="modal-basic-title">{{t('differentVersion')}}</h4>
				</div>
				<div class="modal-body">
					<div class="row justify-content-center">
						<button type="button" class="btn btn-outline-success"
								(click)="reload()">{{t('refresh')}}</button>
					</div>
				</div>
			</ng-container>
		</ng-template>
	`,
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
	title = APP_NAME;
	version = VERSION;
	@HostBinding('class.splashEnabled') get loadingClass() { return this.layout?.splashVisible$?.value; }
	@ViewChild('content', {static: true}) modalRef: NgbModalRef;

	constructor(
		private store: Store,
		private config: NgbPopoverConfig,
		private layout: LayoutService,
		private modal: NgbModal,
		private tabCountService: TabCountService,
		private authService: AuthService
	) {
		this.popoverConfigSetup();
	}

	private handleTokenRefreshAfterPageLoad(): void {
		const revokeToken = sessionStorage.getItem(REVOKE_TOKEN);
		if (!revokeToken) return;

		setTimeout(() => {
			this.authService.refreshToken().pipe(
				untilDestroyed(this),
				take(1),
				finalize(() => this.authService.manageRevokeTokenFlag('remove'))
			).subscribe();
		}, 500);
	}

	ngOnInit(): void {
		this.routerLogging();
		this.store.dispatch(userActions.initUser());
		this.layout.splashControl();
		this.checkVersion();
		window.addEventListener('beforeunload', this.authService.handlePageBeforeunload.bind(this.authService));
		window.addEventListener('load', this.handleTokenRefreshAfterPageLoad.bind(this));
		window.addEventListener('beforeunload', this.tabCountService.handleBeforeUnload.bind(this.tabCountService));
	}

	ngOnDestroy(): void {
		this.layout.splashControl();
	}

	routerLogging() {
		this.store.pipe(select(getCurrentRouteState)).pipe(
			untilDestroyed(this)
		).subscribe(res => {
			if (!res) return;
			this.store.dispatch(rootActions.navigateEnd({url: res.url}));
		});
	}

	popoverConfigSetup() {
		const {autoClose, placement} = POPOVER_CONFIG;
		this.config.autoClose = autoClose;
		this.config.placement = placement;
	}

	checkVersion() {
		this.store.dispatch(userActions.checkVersion({appVersion: this.version}));
		this.versionModal();
	}

	versionModal() {
		this.store.select(selectVersionStatus).pipe(
			untilDestroyed(this)
		).subscribe(latestVersion => {
			if (latestVersion) return;
			this.modal.open(this.modalRef, {ariaLabelledBy: 'modal-basic-title'});
		});
	}

	reload = () => {
		this.modal.dismissAll();
		window.location.reload();
	}
}
